import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import ImageUploader from "@/components/base/ImageUploader";
import { supabase } from "@/lib/supabase";

const departments = ["All", "Management", "Health", "Education", "Economic Security", "Protection", "Operations"];

const deptColors: Record<string, string> = {
  Management: "bg-stone-100 text-stone-700",
  Health: "bg-emerald-100 text-emerald-700",
  Education: "bg-amber-100 text-amber-700",
  "Economic Security": "bg-teal-100 text-teal-700",
  Protection: "bg-rose-100 text-rose-700",
  Operations: "bg-violet-100 text-violet-700",
};

type FormData = {
  name: string;
  role: string;
  department: string;
  email: string;
  phone: string;
  status: "Active" | "Inactive";
  joinDate: string;
  imageUrl: string;
};

const emptyForm: FormData = {
  name: "",
  role: "",
  department: "Management",
  email: "",
  phone: "",
  status: "Active",
  joinDate: "",
  imageUrl: "",
};

function getInitials(name: string) {
  return name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterDept, setFilterDept] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [viewUser, setViewUser] = useState<any | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("staff_members")
      .select("*")
      .order("created_at", { ascending: true });
    if (error) {
      console.error("Error fetching users:", error);
    } else {
      setUsers(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filtered = users.filter((u) => {
    const matchDept = filterDept === "All" || u.department === filterDept;
    const matchStatus = filterStatus === "All" || u.status === filterStatus;
    const matchSearch =
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      (u.role || "").toLowerCase().includes(search.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(search.toLowerCase());
    return matchDept && matchStatus && matchSearch;
  });

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (u: any) => {
    setEditingId(u.id);
    setForm({
      name: u.name,
      role: u.role || "",
      department: u.department,
      email: u.email,
      phone: u.phone || "",
      status: u.status,
      joinDate: u.join_date || "",
      imageUrl: u.image_url || "",
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) return;
    if (editingId !== null) {
      const { error } = await supabase
        .from("staff_members")
        .update({
          name: form.name,
          role: form.role,
          department: form.department,
          email: form.email,
          phone: form.phone,
          status: form.status,
          join_date: form.joinDate,
          image_url: form.imageUrl || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", editingId);
      if (error) {
        console.error("Error updating user:", error);
        return;
      }
    } else {
      const { error } = await supabase.from("staff_members").insert({
        name: form.name,
        role: form.role,
        department: form.department,
        email: form.email,
        phone: form.phone,
        status: form.status,
        join_date: form.joinDate,
        image_url: form.imageUrl || null,
      });
      if (error) {
        console.error("Error creating user:", error);
        return;
      }
    }
    setShowModal(false);
    fetchUsers();
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("staff_members").delete().eq("id", id);
    if (error) {
      console.error("Error deleting user:", error);
      return;
    }
    setDeleteId(null);
    fetchUsers();
  };

  const toggleStatus = async (id: string, current: string) => {
    const next = current === "Active" ? "Inactive" : "Active";
    const { error } = await supabase
      .from("staff_members")
      .update({ status: next })
      .eq("id", id);
    if (error) {
      console.error("Error toggling status:", error);
      return;
    }
    fetchUsers();
  };

  return (
    <AdminLayout title="User Management" subtitle="Manage staff members and their access">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          {
            label: "Total Staff",
            value: users.length,
            icon: "ri-team-line",
            color: "bg-teal-50 text-teal-600",
          },
          {
            label: "Active",
            value: users.filter((u) => u.status === "Active").length,
            icon: "ri-user-follow-line",
            color: "bg-emerald-50 text-emerald-600",
          },
          {
            label: "Inactive",
            value: users.filter((u) => u.status === "Inactive").length,
            icon: "ri-user-unfollow-line",
            color: "bg-stone-100 text-stone-500",
          },
          { label: "Departments", value: 6, icon: "ri-building-line", color: "bg-amber-50 text-amber-600" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-4 border border-stone-100">
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl mb-3 ${s.color}`}>
              <i className={`${s.icon} text-base`}></i>
            </div>
            <p className="text-xl font-bold text-stone-800">{s.value}</p>
            <p className="text-xs text-stone-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-stone-400 text-sm"></i>
          </div>
          <input
            type="text"
            placeholder="Search staff..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:border-primary text-stone-700 placeholder-stone-400"
          />
        </div>
        <select
          value={filterDept}
          onChange={(e) => setFilterDept(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-stone-200 bg-white text-sm text-stone-600 focus:outline-none focus:border-primary cursor-pointer"
        >
          {departments.map((d) => (
            <option key={d}>{d}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-stone-200 bg-white text-sm text-stone-600 focus:outline-none focus:border-primary cursor-pointer"
        >
          <option>All</option>
          <option>Active</option>
          <option>Inactive</option>
        </select>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors duration-200 whitespace-nowrap cursor-pointer ml-auto"
        >
          <i className="ri-user-add-line"></i>
          Add Staff
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-primary text-2xl"></i>
        </div>
      )}

      {/* Grid */}
      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((u) => (
            <div
              key={u.id}
              className="bg-white rounded-2xl border border-stone-100 overflow-hidden hover:border-primary/20 transition-all duration-200"
            >
              <div className="flex items-start justify-between p-4 pb-0">
                <div className="flex items-center gap-3">
                  {u.image_url ? (
                    <img
                      src={u.image_url}
                      alt={u.name}
                      className="w-12 h-12 rounded-full object-cover object-top"
                    />
                  ) : (
                    <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-full">
                      <span className="text-sm font-bold text-primary">{getInitials(u.name)}</span>
                    </div>
                  )}
                  <div>
                    <h3 className="text-sm font-bold text-stone-800 leading-tight">{u.name}</h3>
                    <p className="text-xs text-stone-500">{u.role}</p>
                  </div>
                </div>
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                    u.status === "Active" ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"
                  }`}
                >
                  {u.status}
                </span>
              </div>
              <div className="px-4 pt-2">
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    deptColors[u.department] ?? "bg-stone-100 text-stone-500"
                  }`}
                >
                  {u.department}
                </span>
              </div>
              <div className="mt-4 p-4 pt-3 border-t border-stone-100 flex items-center gap-2">
                <button
                  onClick={() => setViewUser(u)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold text-stone-600 hover:bg-stone-100 cursor-pointer transition-colors"
                >
                  <i className="ri-eye-line"></i> View
                </button>
                <button
                  onClick={() => openEdit(u)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold text-primary hover:bg-primary/10 cursor-pointer transition-colors"
                >
                  <i className="ri-edit-line"></i> Edit
                </button>
                <button
                  onClick={() => setDeleteId(u.id)}
                  className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg cursor-pointer transition-all duration-200"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16">
              <div className="w-12 h-12 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-3">
                <i className="ri-team-line text-stone-400 text-xl"></i>
              </div>
              <p className="text-stone-500 text-sm font-medium">No staff members found</p>
            </div>
          )}
        </div>
      )}

      {/* View Modal */}
      {viewUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-5 border-b border-stone-100">
              <h2 className="text-base font-bold text-stone-800">Staff Profile</h2>
              <button
                onClick={() => setViewUser(null)}
                className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="px-6 py-6">
              <div className="flex flex-col items-center text-center mb-6">
                {viewUser.image_url ? (
                  <img
                    src={viewUser.image_url}
                    alt={viewUser.name}
                    className="w-16 h-16 rounded-full object-cover object-top mb-3"
                  />
                ) : (
                  <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mb-3">
                    <span className="text-xl font-bold text-primary">{getInitials(viewUser.name)}</span>
                  </div>
                )}
                <h3 className="text-base font-bold text-stone-800">{viewUser.name}</h3>
                <p className="text-sm text-stone-500 mt-0.5">{viewUser.role}</p>
                <span
                  className={`mt-2 px-2.5 py-1 rounded-full text-xs font-semibold ${
                    deptColors[viewUser.department] ?? "bg-stone-100 text-stone-500"
                  }`}
                >
                  {viewUser.department}
                </span>
              </div>
              <div className="flex flex-col gap-3">
                {[
                  { icon: "ri-mail-line", label: "Email", value: viewUser.email },
                  { icon: "ri-phone-line", label: "Phone", value: viewUser.phone || "—" },
                  { icon: "ri-calendar-line", label: "Joined", value: viewUser.join_date || "—" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl">
                    <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg shrink-0">
                      <i className={`${item.icon} text-stone-500 text-sm`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-stone-400">{item.label}</p>
                      <p className="text-sm font-medium text-stone-700">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => {
                  setViewUser(null);
                  openEdit(viewUser);
                }}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary-dark cursor-pointer transition-colors"
              >
                Edit Profile
              </button>
              <button
                onClick={() => toggleStatus(viewUser.id, viewUser.status)}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold border border-stone-200 text-stone-600 hover:bg-stone-50 cursor-pointer transition-colors"
              >
                {viewUser.status === "Active" ? "Deactivate" : "Activate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-stone-100">
              <h2 className="text-base font-bold text-stone-800">
                {editingId ? "Edit Staff Member" : "Add Staff Member"}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="px-6 py-5 flex flex-col gap-4">
              {/* Photo */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                  Profile Photo
                </label>
                <ImageUploader
                  value={form.imageUrl}
                  onChange={(url) => setForm({ ...form, imageUrl: url })}
                  bucket="company-assets"
                  folder="our-team"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Full name"
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Role / Title</label>
                  <input
                    type="text"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    placeholder="e.g. Field Coordinator"
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Department</label>
                  <select
                    value={form.department}
                    onChange={(e) => setForm({ ...form, department: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-700 cursor-pointer"
                  >
                    {["Management", "Health", "Education", "Economic Security", "Protection", "Operations"].map(
                      (d) => (
                        <option key={d}>{d}</option>
                      )
                    )}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="email@bhodevcameroon.org"
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Phone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+237 000 000 000"
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Join Date</label>
                  <input
                    type="date"
                    value={form.joinDate}
                    onChange={(e) => setForm({ ...form, joinDate: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value as "Active" | "Inactive" })}
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-700 cursor-pointer"
                  >
                    <option>Active</option>
                    <option>Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-100">
              <button
                onClick={() => setShowModal(false)}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold text-stone-600 hover:bg-stone-100 cursor-pointer transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!form.name.trim() || !form.email.trim()}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary-dark cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingId ? "Save Changes" : "Add Staff"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
              <i className="ri-user-unfollow-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-stone-800 text-center mb-2">Remove Staff Member?</h3>
            <p className="text-sm text-stone-500 text-center mb-6">
              This will permanently remove this staff member from the system.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-stone-600 border border-stone-200 hover:bg-stone-50 cursor-pointer transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteId)}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold bg-red-500 text-white hover:bg-red-600 cursor-pointer transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}